import React from 'react';
import { Card, CardBody, Spinner } from 'reactstrap';
import { getAppContext } from '~/lib/AppContext';
import './RedirectCard.scss';

type RedirectCardProps = {
    title?: string;
    message: string;
    /** Rendered below the message, in place of the spinner: the page is waiting on the reader, not the browser. */
    children?: React.ReactNode;
};

/**
 * Full-page card for a page that hands the visitor off elsewhere: shown while the redirect happens, or,
 * with `children`, while it waits for the visitor to continue (PortalRedirect's support code). Rendered
 * under PreLoginLayout.
 *
 * The centring and the width cap live in RedirectCard.scss rather than in any page's own stylesheet,
 * deliberately: every redirect page is meant to read as one component, and a page that styled its own
 * card would drift from the rest. So a change here lands on all of them — screenshot each page that
 * renders it, per the MR checklist in PanelSite.md.
 */
export default function RedirectCard({ title, message, children }: RedirectCardProps) {
    const appConfig = getAppContext().config;

    return (
        <div className="redirectCard">
            <Card>
                <CardBody className="text-center">
                    <div className="logo">
                        <img src={`/img/logo-${appConfig.theme.toLowerCase()}.png`} alt={appConfig.brandName} />
                    </div>
                    {title && <h3>{title}</h3>}
                    {/* role="status" (implicit aria-live) so screen readers announce the redirect */}
                    <p className="mt-4" role="status">
                        {message}
                    </p>
                    {children ?? <Spinner role="presentation" aria-hidden="true"></Spinner>}
                </CardBody>
            </Card>
        </div>
    );
}
